import { useMemo } from 'react';

import Tabs from 'approot/shared/tabs/tabs';
import { ClassroomAnswerTabs as ClassroomAnswerTabsStyled } from './classroom-answer.style';
import { NegativeButton } from 'approot/shared/styles/buttons/negative-button';
import ClearModelledResponseModal from 'approot/activity-builder/components/shared/sheet/clear-modelled-response-modal';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';

export const QUESTION_VIEW = 'questionView';
export const CLASS_VIEW = 'classView';
export const INDIVIDUAL_VIEW = 'individualView';
export const SUGGESTED_VIEW = 'suggestedView';

type Props = {
  isPublished: boolean;
  includeClassResponse?: boolean;
  includeIndividualAnswers?: boolean;
  includeSuggestedAnswers?: boolean;
  tab: string;
  onSelect: (option: string) => void;
  componentId: string;
  onCloseActivitySheet?: () => void;
  canClearModelledResponse?: boolean;
};

const ClassroomAnswerTabs = ({
  isPublished,
  includeClassResponse = true,
  includeSuggestedAnswers = false,
  includeIndividualAnswers = true,
  tab,
  onSelect,
  componentId,
  onCloseActivitySheet,
  canClearModelledResponse = true,
}: Props) => {
  const tabOptions = useMemo(() => {
    const options = [];
    if (isPublished) {
      if (includeIndividualAnswers) {
        options.push({ label: 'Student Responses', value: INDIVIDUAL_VIEW });
      }
      options.push({ label: 'Model Response', value: QUESTION_VIEW });
      if (includeClassResponse) {
        options.push({ label: 'Class Responses', value: CLASS_VIEW });
      }
      if (includeSuggestedAnswers) {
        options.push({ label: 'Suggested Answers', value: SUGGESTED_VIEW });
      }
    } else {
      options.push({ label: 'Model Response', value: QUESTION_VIEW });
    }
    return options;
  }, [
    isPublished,
    includeClassResponse,
    includeIndividualAnswers,
    includeSuggestedAnswers,
  ]);

  const {
    isVisible: isClearModalVisible,
    openModal: openClearModal,
    closeModal: closeClearModal,
  } = useSimpleModalState();

  // isPublished passed via props can mean "view as if published"
  const { isPublished: canRespond } = useActivityBuilderPresentContext();

  return (
    <>
      <ClassroomAnswerTabsStyled id="responseTabs">
        <Tabs
          activeTab={tab}
          ariaDescription="Response tabs"
          options={tabOptions}
          contentPanelId="content"
          onSelect={option => {
            onSelect(option.value as string);
          }}
        />
        {tab === QUESTION_VIEW && canRespond && canClearModelledResponse && (
          <NegativeButton
            type="button"
            variant="outlined"
            data-selector="clear-modelled-response-button"
            onClick={openClearModal}
          >
            Clear Modelled Response
          </NegativeButton>
        )}
      </ClassroomAnswerTabsStyled>
      <ClearModelledResponseModal
        isVisible={isClearModalVisible}
        onClose={closeClearModal}
        componentId={componentId}
        onCloseActivitySheet={onCloseActivitySheet}
      />
    </>
  );
};

export default ClassroomAnswerTabs;
